import type {
  IErrorApiResponse,
  IPaginationResponse,
  ISuccessApiResponse,
} from '@wyz/types';
import { notify } from '@wyz/ui';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serialize,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig, useI18n } from '~/common/context';
import { useCommonErrorTranslation } from '~/common/hooks';
import { CommonErrorCodesEnumType, ILanguage } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';

type Options = {
  getWyzApiLanguages?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<ILanguage>>
  > & {
    args?: {
      active?: boolean;
    };
  };
  activate?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<unknown, CommonErrorCodesEnumType>,
    { isoCode: string }
  >;
  deactivate?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<unknown, CommonErrorCodesEnumType>,
    { isoCode: string }
  >;
  toggle?: {
    enabled?: boolean;
  };
};

export const useManageLanguages = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const getWyzApiLanguagesQuery = useQuery({
    queryKey: ConnectQueryKeys.languages.base(options.getWyzApiLanguages?.args),
    queryFn: () =>
      callCoreApi<IPaginationResponse<ILanguage>>(
        `/settings/api/languages?${serialize(options.getWyzApiLanguages?.args)}`,
        {},
      ),
    retry: false,
    refetchOnMount: true,
    ...options.getWyzApiLanguages,
  });

  const activateMutation = useMutation({
    mutationFn: ({ isoCode }) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/languages/${isoCode}/activate`,
        {
          method: 'PATCH',
        },
      ),
    keysToInvalidateOnSuccess: [ConnectQueryKeys.languages.activeByTenant()],
    ...options.activate,
  });

  const deactivateMutation = useMutation({
    mutationFn: ({ isoCode }) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/languages/${isoCode}/deactivate`,
        {
          method: 'PATCH',
        },
      ),
    keysToInvalidateOnSuccess: [ConnectQueryKeys.languages.activeByTenant()],
    ...options.deactivate,
  });

  const toggle = (isoCode: string, activate: boolean) => {
    if (activate) {
      activateMutation.mutate(
        { isoCode },
        {
          onSuccess: () => {
            notify.success(t('app.language.messages.activated'), {
              id: 'toast-success-language-activated',
            });
          },
          onError: (error) => {
            notify.error(t(resolveErrorKey(error.message)), {
              id: 'toast-error-language-activation-failed',
            });
          },
        },
      );
    } else {
      deactivateMutation.mutate(
        { isoCode },
        {
          onSuccess: () => {
            notify.success(t('app.language.messages.deactivated'), {
              id: 'toast-success-language-deactivated',
            });
          },
          onError: (error) => {
            notify.error(t(resolveErrorKey(error.message)), {
              id: 'toast-error-language-deactivation-failed',
            });
          },
        },
      );
    }
  };

  return restrictApi(options, {
    getWyzApiLanguages: getWyzApiLanguagesQuery,
    // getTenantLanguages: getTenantLanguagesQuery,
    toggle: {
      mutate: toggle,
      isLoading: activateMutation.isLoading || deactivateMutation.isLoading,
    },
  });
};
